// ---------------------------------------------------------------------------
// V14 — THE CREW PLANNING BOARD'S VIEW MODEL. ONE DATA LAYER, TWO RENDERERS.
//
// 12-… §V14.2 settles the architecture in one sentence: "both renderers consume
// one view model. Write it once." Grid renders these rows as table rows,
// Timeline renders THE SAME rows as positioned bars, and everything either of
// them shows is computed here or not at all.
//
// THE FILE IS PURE AND IT IS IN THE NODE TEST BUILD, which is the same argument
// tsconfig.test.json makes for system/crew.ts: "the rules that matter most are
// decided in files a node test can execute, rather than inside a component."
// A board is a dense, virtualised, keyboard-driven surface, and almost none of
// what makes it right or wrong is visual.
//
// FOUR RULES THIS FILE KEEPS, EACH OF WHICH IS A CHECK IN verify-V14.sh:
//
//   1. IT DOES NOT COMPUTE READINESS. Not the fill count, not the shortfall,
//      not the contribution. Every one of those arrives from
//      GET /v1/events/:id/readiness and is copied through unaltered. §V14.2's
//      whole argument: "a board that says 3/4 while the score says otherwise
//      destroys trust in both". board.test.ts feeds it a readiness payload that
//      CONTRADICTS the assignments and asserts the board repeats the payload.
//   2. IT NAMES NO STATE. Two booleans travel with every assignment — does this
//      state hold a place, is it terminal — and both questions are asked of
//      those. The same rule V13's crew list keeps, and verify-V13 §7 greps for
//      breaches of it across the whole of web/src.
//   3. IT CARRIES NO COLOUR. `crew.ts` decides tone from behaviour; this file
//      decides words. 11-… §6 measured the four semantic colours at 1.02:1 to
//      1.72:1 of each other, so in greyscale they are one colour.
//   4. NOTHING HERE INVITES FREE TEXT. A blocker's sentence is assembled by the
//      server from counts and a role name; the board never composes one and has
//      nowhere to type one.
// ---------------------------------------------------------------------------

import { accessibleName, type CrewRow } from "./crew";
import { english, type Translate } from "../i18n/translate";
import type { MessageKey } from "../i18n/translate";

// ---------------------------------------------------------------------------
// What arrives. Structural types rather than imports from api/console.ts, for
// crew.ts's reason: api/console.ts is not in the node test build and pulling it
// in would drag the whole client along to test arithmetic. TypeScript checks
// the real rows satisfy these at the call site in the screen, which is the
// guarantee that mattered.
// ---------------------------------------------------------------------------

/** V11's requirement, reduced to what a board row needs. */
export type BoardRequirement = {
  id: string;
  role_name: string;
  zone: string | null;
  headcount: number;
  criticality: number;
  criticality_label: string;
  starts_at: string;
  ends_at: string;
};

/** V13's roster row. `CrewRow` is the half crew.ts already speaks for. */
export type BoardCrew = CrewRow & {
  id: string;
  worker_id: string;
  requirement_id: string | null;
  starts_at: string;
  ends_at: string;
  status: string;
};

export type BoardBlocker = { code: string; sentence: string };

/** V14's readiness row, copied through untouched. */
export type BoardReadiness = {
  requirement_id: string;
  headcount: number;
  filled: number;
  reserves: number;
  shortfall: number;
  contribution: number;
  counts_toward_readiness: boolean;
  blocks_opening: boolean;
  blockers: BoardBlocker[];
};

// ---------------------------------------------------------------------------
// The view model.
// ---------------------------------------------------------------------------

export type BoardAssignment = {
  id: string;
  workerId: string;
  workerName: string;
  roleName: string;
  stateLabel: string;
  fillsHeadcount: boolean;
  isTerminal: boolean;
  isReserve: boolean;
  startsAt: string;
  endsAt: string;
  /** The opaque state id, carried so a transition can be requested for it. */
  state: string;
};

export type BoardRow = {
  /**
   * null on the ONE row that is not a requirement.
   *
   * An assignment may exist with no requirement — V13 made `requirement_id`
   * ON DELETE SET NULL precisely so that removing a requirement detaches
   * twenty people rather than erasing them. Those people are still booked, they
   * still turn up, and a board that dropped them would be the most dangerous
   * kind of clean.
   */
  requirementId: string | null;
  post: string;
  role: string;
  criticality: number;
  criticalityLabel: string;
  startsAt: string;
  endsAt: string;
  headcount: number;
  assignments: BoardAssignment[];
  /**
   * From the readiness endpoint, or null when it did not answer.
   *
   * NULL IS NOT ZERO AND THE DISTINCTION IS THE WHOLE POINT. A board that
   * showed "0 of 4" when the readiness call was refused would be reporting a
   * dark post, which is a lie in the dangerous direction — somebody would go
   * and staff a post that is already full. The screen renders null as words.
   */
  readiness: BoardReadiness | null;
};

/**
 * One place on the board: a person, or a place with nobody in it.
 *
 * VACANCIES ARE CELLS AND NOT A COUNT, because §V14.4 makes the arrow keys move
 * "cell focus" and §6 wants a person dropped onto an empty place. A number in a
 * corner is not something a cursor can land on.
 *
 * How many vacancies there are is READ FROM READINESS, never subtracted here.
 */
export type BoardCell =
  | { kind: "assignment"; key: string; assignment: BoardAssignment }
  | { kind: "vacancy"; key: string; index: number }
  /** Readiness did not answer, so how many places are open is not known. */
  | { kind: "unknown"; key: string };

const UNATTACHED_POST = "Not attached to a requirement";

/** A person's row, as V13's list gives it, mapped once. */
function toAssignment(row: BoardCrew): BoardAssignment {
  return {
    id: row.id,
    workerId: row.worker_id,
    workerName: row.worker_name,
    roleName: row.role_name,
    stateLabel: row.status_label,
    fillsHeadcount: row.status_fills_headcount,
    isTerminal: row.status_is_terminal,
    isReserve: row.is_reserve,
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    state: row.status,
  };
}

/**
 * THE VIEW MODEL. One function, called once, feeding both renderers.
 *
 * Requirements come from V11, assignments from V13, readiness from V14 — three
 * endpoints because each is somebody's else's business and merging them into a
 * fourth would mean a board endpoint that has to be kept in step with all three.
 * They are joined HERE, in a pure function a node test can run, rather than in
 * a component nothing can.
 */
export function buildBoard(input: {
  requirements: readonly BoardRequirement[];
  crew: readonly BoardCrew[];
  /** null when the readiness call did not answer at all. */
  readiness: readonly BoardReadiness[] | null;
}): BoardRow[] {
  const byRequirement = new Map<string, BoardAssignment[]>();
  const unattached: BoardAssignment[] = [];
  for (const row of input.crew) {
    const mapped = toAssignment(row);
    if (row.requirement_id === null) {
      unattached.push(mapped);
      continue;
    }
    const bucket = byRequirement.get(row.requirement_id);
    if (bucket) bucket.push(mapped);
    else byRequirement.set(row.requirement_id, [mapped]);
  }

  const readinessById = new Map<string, BoardReadiness>();
  for (const r of input.readiness ?? []) readinessById.set(r.requirement_id, r);

  const rows: BoardRow[] = input.requirements.map((requirement) => ({
    requirementId: requirement.id,
    post: requirement.zone ?? requirement.role_name,
    role: requirement.role_name,
    criticality: requirement.criticality,
    criticalityLabel: requirement.criticality_label,
    startsAt: requirement.starts_at,
    endsAt: requirement.ends_at,
    headcount: requirement.headcount,
    assignments: byRequirement.get(requirement.id) ?? [],
    // COPIED, NOT COMPUTED. `?? null` and never `?? {filled: 0, …}`: a default
    // here would silently turn "the readiness call was refused" into "this post
    // is empty", which is rule 1 of this file being broken by a fallback.
    readiness: readinessById.get(requirement.id) ?? null,
  }));

  // THE DETACHED PEOPLE, LAST AND ALWAYS PRESENT WHEN THERE ARE ANY. Nobody is
  // asking for these places any more, so there is no number of them to ask for
  // and no requirement left to score.
  if (unattached.length > 0) {
    rows.push({
      requirementId: null,
      post: UNATTACHED_POST,
      role: unattached[0]!.roleName,
      criticality: 0,
      criticalityLabel: "Not stated",
      startsAt: unattached[0]!.startsAt,
      endsAt: unattached[0]!.endsAt,
      headcount: 0,
      assignments: unattached,
      readiness: null,
    });
  }

  return rows;
}

/**
 * The cells of one row, in the order a cursor walks them.
 *
 * PEOPLE FIRST, THEN VACANCIES. A scheduler scanning left to right is looking
 * for the gap, and putting the gaps at a predictable end of the row is what
 * makes that scan work at forty rows.
 */
export function cellsFor(row: BoardRow): BoardCell[] {
  const cells: BoardCell[] = row.assignments.map((assignment) => ({
    kind: "assignment" as const,
    key: assignment.id,
    assignment,
  }));
  if (row.readiness === null) {
    // NOT ZERO VACANCIES. "I do not know how many places are open" and "no
    // places are open" are different sentences and the board must say which.
    if (row.requirementId !== null) cells.push({ kind: "unknown", key: `${row.requirementId}:unknown` });
    return cells;
  }
  for (let i = 0; i < row.readiness.shortfall; i += 1) {
    cells.push({ kind: "vacancy", key: `${row.requirementId}:vacancy:${i}`, index: i });
  }
  return cells;
}

/**
 * The fill sentence, in words, for a row.
 *
 * IT IS THE READINESS PAYLOAD READ ALOUD. Not one arithmetic operation happens
 * here — `filled`, `headcount` and `shortfall` all arrive already decided, and
 * board.test.ts proves it by supplying three that do not add up and asserting
 * all three are printed as given.
 */
// One fill sentence per row and translator. cellName asks for it once per cell, and a row
// carries every cell of its post, so at 2,000 bookings this is the board's hot path (V14).
const FILL_SENTENCES = new WeakMap<BoardRow, { t: Translate; text: string }>();

export function fillSentence(row: BoardRow, t: Translate = english): string {
  const kept = FILL_SENTENCES.get(row);
  if (kept !== undefined && kept.t === t) return kept.text;
  const text = composeFillSentence(row, t);
  FILL_SENTENCES.set(row, { t, text });
  return text;
}

function composeFillSentence(row: BoardRow, t: Translate): string {
  if (row.requirementId === null) return t("Detached from its requirement");
  if (row.readiness === null) return t("Fill unknown — the readiness figure did not load");
  const { filled, headcount, shortfall, reserves } = row.readiness;
  const standby = reserves === 0 ? "" : t(", {n} on standby", { n: reserves });
  if (shortfall === 0) return t("{filled} of {headcount} filled", { filled, headcount }) + standby;
  return t("{filled} of {headcount} filled, {shortfall} unfilled", { filled, headcount, shortfall }) + standby;
}

/**
 * S145. A blocker's sentence in the reader's language: the server's template through
 * `t`, or its English when it sent none. api/test/readiness-score.test.ts holds every
 * template to hu.ts, so the cast names a key the catalogue carries.
 */
function blockerSentence(b: { sentence: string; sentence_key?: string; params?: Record<string, string | number> }, t: Translate): string {
  return b.sentence_key ? t(b.sentence_key as MessageKey, (b.params ?? {}) as never) : b.sentence;
}

/**
 * The accessible name of one cell — §V14.5, state AND cause, never colour.
 *
 * The example the spec gives is "Turnstile 14, turnstile operator, 3 of 4
 * filled, 1 unfilled, pool exhausted", and it has four parts: where, what,
 * how full, and WHY IT IS NOT FULLER. The last part is the one a colour can
 * never carry, and it is the one this board exists to surface.
 *
 * The person half is `crew.ts`'s `accessibleName`, unchanged and reused — the
 * V13 list and the V14 board must not describe the same booking two ways.
 */
export function cellName(row: BoardRow, cell: BoardCell, crew?: CrewRow, t: Translate = english): string {
  const where = `${row.post}, ${row.role}`;
  const fill = fillSentence(row, t);
  const cause = (row.readiness?.blockers ?? []).map((b) => blockerSentence(b, t)).join(" ");
  if (cell.kind === "assignment") {
    // The crew row is passed through so the sentence is the SAME one the list
    // screen speaks. Falling back keeps the board renderable from a board row
    // alone, which is what the timeline does.
    const person =
      crew ??
      ({
        worker_name: cell.assignment.workerName,
        role_name: cell.assignment.roleName,
        status_label: cell.assignment.stateLabel,
        status_fills_headcount: cell.assignment.fillsHeadcount,
        status_is_terminal: cell.assignment.isTerminal,
        is_reserve: cell.assignment.isReserve,
      } satisfies CrewRow);
    return `${where}. ${accessibleName(person, t)}. ${fill}.${cause ? ` ${cause}` : ""}`;
  }
  if (cell.kind === "unknown") {
    return `${where}. ${t("An open place cannot be counted because the readiness figure did not load.")}`;
  }
  return `${where}. ${t("Open place {n}, nobody assigned.", { n: cell.index + 1 })} ${fill}.${cause ? ` ${cause}` : ""}`;
}

// ---------------------------------------------------------------------------
// FILTERING AND GROUPING — §V14.6's "filter by state, role, zone, criticality ·
// group by zone or role".
// ---------------------------------------------------------------------------

export type BoardFilter = {
  /** An opaque state id from the server's vocabulary. Never a word spelled here. */
  state: string | null;
  role: string | null;
  zone: string | null;
  criticality: number | null;
  /** The `/` box. Matches the post, the role, or any person's name. */
  text: string;
};

export const NO_FILTER: BoardFilter = { state: null, role: null, zone: null, criticality: null, text: "" };

export function isFiltered(filter: BoardFilter): boolean {
  return (
    filter.state !== null ||
    filter.role !== null ||
    filter.zone !== null ||
    filter.criticality !== null ||
    filter.text.trim() !== ""
  );
}

/**
 * Rows matching the filter.
 *
 * A STATE FILTER NARROWS THE ASSIGNMENTS AND KEEPS THE ROW, which is not the
 * obvious implementation and is the right one: filtering to a state somebody
 * has fallen out at, and being shown only the posts where that happened, is how
 * a scheduler finds the holes. Dropping the row entirely would answer a
 * different question — and naming the state here rather than describing it
 * would put a copy of the vocabulary in the browser, which is the rule this
 * file's header sets and the rule a comment about it keeps breaking.
 */
export function filterRows(rows: readonly BoardRow[], filter: BoardFilter): BoardRow[] {
  const needle = filter.text.trim().toLowerCase();
  const out: BoardRow[] = [];
  for (const row of rows) {
    if (filter.role !== null && row.role !== filter.role) continue;
    if (filter.zone !== null && row.post !== filter.zone) continue;
    if (filter.criticality !== null && row.criticality !== filter.criticality) continue;

    let assignments = row.assignments;
    if (filter.state !== null) {
      assignments = assignments.filter((a) => a.state === filter.state);
      if (assignments.length === 0) continue;
    }
    if (needle !== "") {
      const inRow = row.post.toLowerCase().includes(needle) || row.role.toLowerCase().includes(needle);
      const matching = assignments.filter((a) => a.workerName.toLowerCase().includes(needle));
      if (!inRow && matching.length === 0) continue;
      if (!inRow) assignments = matching;
    }
    out.push(assignments === row.assignments ? row : { ...row, assignments });
  }
  return out;
}

export type BoardGrouping = "none" | "zone" | "role";
export type BoardGroup = { key: string; label: string; rows: BoardRow[] };

/** Grouped rows, in first-appearance order so the time ordering survives. */
export function groupRows(rows: readonly BoardRow[], grouping: BoardGrouping, t: Translate = english): BoardGroup[] {
  if (grouping === "none") return [{ key: "all", label: t("All posts"), rows: [...rows] }];
  const groups: BoardGroup[] = [];
  const index = new Map<string, BoardGroup>();
  for (const row of rows) {
    const label = grouping === "zone" ? row.post : row.role;
    let group = index.get(label);
    if (!group) {
      group = { key: label, label, rows: [] };
      index.set(label, group);
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
}

/** Every distinct value of one facet, for the filter selects. */
export function facet(rows: readonly BoardRow[], of: "role" | "zone"): string[] {
  const seen: string[] = [];
  for (const row of rows) {
    const value = of === "role" ? row.role : row.post;
    if (!seen.includes(value)) seen.push(value);
  }
  return seen.sort((a, b) => a.localeCompare(b));
}

// ---------------------------------------------------------------------------
// VIRTUALISATION — §V14.3. "Virtualise rows. Do not virtualise the time axis in
// the timeline view — horizontal extent is bounded by the event, and
// virtualising it breaks the at-a-glance overlap reading that is the view's
// entire purpose."
// ---------------------------------------------------------------------------

export type BoardWindow = {
  start: number;
  end: number;
  /** Pixels of empty space above and below, so the scrollbar stays honest. */
  padTop: number;
  padBottom: number;
};

/**
 * Which rows to render, given where the scroller is.
 *
 * OVERSCAN IS NOT COSMETIC. Rendering exactly the visible rows means the row
 * under a keyboard cursor moving one step past the edge does not exist yet, and
 * `scrollIntoView` on a missing element does nothing — the cursor appears to
 * stick. Four rows of slack is cheap and removes the class.
 */
export function visibleWindow(
  total: number,
  scrollTop: number,
  viewportHeight: number,
  rowHeight: number,
  overscan = 4,
): BoardWindow {
  if (rowHeight <= 0 || total <= 0) return { start: 0, end: total, padTop: 0, padBottom: 0 };
  // CLAMPED TO `total` AS WELL AS TO ZERO, and the upper clamp is not
  // defensive tidiness — it is the case that happens. A filter that removes
  // nine hundred rows leaves the scroller's `scrollTop` pointing at where row
  // 940 used to be for one frame, and without this the window would compute a
  // start beyond the end: `start > end`, a padTop of thirty thousand pixels,
  // and a board that renders as a tall empty box. Found by the test asserting
  // `start <= end`, which was written because it looked too obvious to fail.
  const first = Math.max(0, Math.min(total, Math.floor(scrollTop / rowHeight) - overscan));
  const visible = Math.ceil(viewportHeight / rowHeight) + overscan * 2;
  const last = Math.min(total, first + visible);
  return {
    start: first,
    end: last,
    padTop: first * rowHeight,
    padBottom: Math.max(0, (total - last) * rowHeight),
  };
}

// ---------------------------------------------------------------------------
// TIMELINE GEOMETRY — the same rows, positioned.
// ---------------------------------------------------------------------------

export type TimelineExtent = { from: number; to: number };

/** The event's own span, from the rows. Never virtualised — §V14.3. */
export function timelineExtent(rows: readonly BoardRow[]): TimelineExtent | null {
  let from = Number.POSITIVE_INFINITY;
  let to = Number.NEGATIVE_INFINITY;
  for (const row of rows) {
    const start = Date.parse(row.startsAt);
    const end = Date.parse(row.endsAt);
    if (Number.isNaN(start) || Number.isNaN(end)) continue;
    if (start < from) from = start;
    if (end > to) to = end;
  }
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return null;
  return { from, to };
}

/** Where one row's bar sits, as percentages of the extent. */
export function barGeometry(row: BoardRow, extent: TimelineExtent): { leftPct: number; widthPct: number } {
  const span = extent.to - extent.from;
  const start = Date.parse(row.startsAt);
  const end = Date.parse(row.endsAt);
  if (span <= 0 || Number.isNaN(start) || Number.isNaN(end)) return { leftPct: 0, widthPct: 100 };
  // THE WIDTH IS SETTLED FIRST AND THE BAR IS THEN PUSHED TO FIT IT, which is
  // the opposite of the obvious order and is the only one that holds.
  //
  // A FLOOR ON THE WIDTH is necessary: a ten-minute shift inside a three-day
  // event is 0.2% wide, which renders as nothing, and a post that vanishes from
  // a board reads as a post that does not exist. But clamping the left edge
  // first and THEN applying the floor pushes the last shift of the last day
  // past the end of the lane — 99.8% + 1.5% — and it overhangs by an amount
  // nobody would notice on a fixture whose shifts all start in the morning.
  // Caught by a test that asked for a ten-minute shift at the very end.
  const width = Math.max(MIN_BAR_PCT, Math.min(100, ((end - start) / span) * 100));
  const left = Math.max(0, Math.min(100 - width, ((start - extent.from) / span) * 100));
  return { leftPct: left, widthPct: width };
}

/** Narrow enough to read as brief, wide enough to be seen and clicked. */
const MIN_BAR_PCT = 1.5;

// ---------------------------------------------------------------------------
// SELECTION — §V14.6's multi-select, and §V14.7 check 7's round trip.
// ---------------------------------------------------------------------------

/** A cursor position. Row index into the FILTERED rows, cell index into the row. */
export type BoardFocus = { row: number; cell: number };

/**
 * Move the cursor, clamped to what exists.
 *
 * CLAMPED AND NOT WRAPPED. Wrapping at the end of a row puts the cursor
 * somewhere the eye is not, and a scheduler holding right-arrow to scan a post
 * would silently start scanning the next one.
 */
export function moveFocus(
  focus: BoardFocus,
  direction: "up" | "down" | "left" | "right",
  rowCellCounts: readonly number[],
): BoardFocus {
  if (rowCellCounts.length === 0) return { row: 0, cell: 0 };
  const clampRow = (n: number) => Math.max(0, Math.min(rowCellCounts.length - 1, n));
  if (direction === "up" || direction === "down") {
    const row = clampRow(focus.row + (direction === "down" ? 1 : -1));
    const width = Math.max(1, rowCellCounts[row] ?? 1);
    return { row, cell: Math.max(0, Math.min(width - 1, focus.cell)) };
  }
  const row = clampRow(focus.row);
  const width = Math.max(1, rowCellCounts[row] ?? 1);
  const cell = Math.max(0, Math.min(width - 1, focus.cell + (direction === "right" ? 1 : -1)));
  return { row, cell };
}

/** The cell counts both renderers walk. Shared, so the two cursors agree. */
export function cellCounts(rows: readonly BoardRow[]): number[] {
  return rows.map((row) => Math.max(1, cellsFor(row).length));
}

export function toggleSelection(selected: readonly string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id];
}
